import {useNavigate, useParams} from 'react-router'
import {ReactComponent as Asterisk} from '../../../../assets/Icons/Asterisk.svg'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {ErrorLabel} from '../../../../components/ErrorLabel/ErrorLabel'
import {CustomSwitch} from '../../../../components/CustomSwitch/CustomSwitch'
import {uploadDocumentToAWS} from '../../../../utils/evaporateUtil'
import {AWS_FILE_URL} from '../../../../constants/apiConstants'
import {conversationsPath} from '../../../../constants/pathConstants'
import {FileUploader} from 'react-drag-drop-files'
import {LoadingPage} from '../../../LoadingPage'
import {DialogImageUploader} from './DialogImageUploader/DialogImageUploader'
import {dialogApi} from '../../../../api/dialogApi'
import {userSelectors} from '../../../../store/user/userSelectors'
import {cx} from '../../../../utils/classNameUtil'
import {
  useWuShowToast,
  WuButton,
  WuIcon,
  WuInput,
} from '@npm-questionpro/wick-ui-lib'
import styles from './DialogSetup.module.scss'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../../../Translation/English/global'
import {DIALOG} from '../../../../Translation/English/dialog'

interface IDialogSetupProps {
  uploadImages?: boolean
  closeModal?: () => void
  setContent?: (content: React.ReactNode) => void
}

interface IDialogState {
  id?: string
  title: string
  questionText: string
  enableResponse: boolean
  enableLikes: boolean
  enableDislikes: boolean
  coverImage?: string | null
  logoImage?: string | null
}

interface IErrors {
  name: boolean
  duplicateTitle: boolean
}

export const DialogSetup: React.FC<IDialogSetupProps> = ({
  uploadImages = true,
  closeModal,
  setContent,
}) => {
  const selectedWorkforce = userSelectors.useSelectedWorkforce()
  const workforceId = String(selectedWorkforce?.workforceID ?? '')
  const createDialogMutation = dialogApi.useCreateDialog()
  const updateDialogMutation = dialogApi.useUpdateDialog()
  const {dialogId} = useParams<{dialogId: string}>()
  const navigate = useNavigate()
  const wt = useWt()
  const {showToast} = useWuShowToast()
  const {data: dialogDetails, isLoading: isDialogsLoading} =
    dialogApi.useDialogDetails(dialogId ?? '')
  const {data: allDialogsData} = dialogApi.useDialogs(false, workforceId, 1)
  const allDialogs = useMemo(
    () => allDialogsData?.dialogs ?? [],
    [allDialogsData],
  )

  const fileTypes = useMemo(() => ['JPG', 'PNG', 'JPEG', 'SVG'], [])

  const [initialState, setInitialState] = useState<IDialogState>({
    title: '',
    questionText: '',
    enableResponse: uploadImages ? !!dialogDetails?.enableResponse : false,
    enableLikes: uploadImages ? !!dialogDetails?.enableLikes : false,
    enableDislikes: uploadImages ? !!dialogDetails?.enableDislikes : false,
  })

  useEffect(() => {
    if (uploadImages && dialogDetails)
      setInitialState({
        title: dialogDetails.title ?? '',
        questionText: dialogDetails.questionText ?? '',
        enableResponse: !!dialogDetails.enableResponse,
        enableLikes: !!dialogDetails.enableLikes,
        enableDislikes: !!dialogDetails.enableDislikes,
        coverImage: dialogDetails.coverImage || '',
        logoImage: dialogDetails.logoImage || '',
      })
  }, [dialogDetails, uploadImages])

  const [errors, setErrors] = useState<IErrors>({
    name: false,
    duplicateTitle: false,
  })

  const [, setFileLoading] = useState<boolean>(false)
  const [logoFileLoading, setLogoFileLoading] = useState<boolean>(false)
  const [coverFileLoading, setCoverFileLoading] = useState<boolean>(false)

  const changeDialogData = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {name, value} = e.target

      setInitialState(prevState => ({
        ...prevState,
        [name]: value,
      }))

      if (name === 'title') {
        const isDuplicate = allDialogs?.some(
          dialog =>
            dialog.id !== dialogDetails?.id &&
            dialog.title?.toLowerCase().trim() === value.toLowerCase().trim(),
        )

        setErrors({
          name: value.trim().length === 0,
          duplicateTitle: isDuplicate,
        })
      }
    },
    [allDialogs, dialogDetails?.id],
  )

  const permissionSwitch = useCallback((key: keyof IDialogState) => {
    setInitialState(prevState => ({
      ...prevState,
      [key]: !prevState[key],
    }))
  }, [])

  const setField = useCallback(
    (key: keyof IDialogState, value: string | null) => {
      setInitialState(prevState => ({
        ...prevState,
        [key]: value,
      }))
    },
    [],
  )

  const onFileUpload = useCallback(
    async (file: File, name: string) => {
      try {
        if (name === 'logoImage') setLogoFileLoading(true)
        else setCoverFileLoading(true)
        setFileLoading(true)
        const uploadFile = await uploadDocumentToAWS({
          file,
          id: initialState.id,
        })
        setField(name as keyof IDialogState, uploadFile.fileName)
        setFileLoading(false)
        if (name === 'logoImage') setLogoFileLoading(false)
        else setCoverFileLoading(false)
      } catch {
        setFileLoading(false)
        if (name === 'logoImage') setLogoFileLoading(false)
        else setCoverFileLoading(false)
      }
    },
    [initialState.id, setField],
  )

  const dialogAction = useCallback(async () => {
    if (!uploadImages) {
      const res = await createDialogMutation
        .mutateAsync({
          initialState: initialState as unknown as Record<string, unknown>,
        })
        .catch(() => {
          showToast({
            message: wt(DIALOG.failedToCreateConversation),
            variant: 'error',
          })
          return null
        })

      if (!res) return

      const newDialogId = res?.id
      if (newDialogId) {
        navigate({pathname: `${conversationsPath}/${newDialogId}`})
        closeModal?.()
      }
    } else {
      await updateDialogMutation
        .mutateAsync({
          id: dialogDetails?.id,
          ...initialState,
        })
        .catch(() => {
          showToast({
            message: wt(DIALOG.failedToUpdateConversation),
            variant: 'error',
          })
        })
    }
  }, [
    showToast,
    wt,
    uploadImages,
    createDialogMutation,
    updateDialogMutation,
    initialState,
    dialogDetails?.id,
    navigate,
    closeModal,
  ])

  const switchBlock = (): React.ReactElement => {
    return (
      <div className={styles.switches}>
        <div className={styles.switch} data-testid="dialog-comments-toggle">
          <CustomSwitch
            isChecked={initialState.enableResponse || false}
            handleChange={() => permissionSwitch('enableResponse')}
            label={`${wt(DIALOG.comments)}`}
          />
        </div>
        <div className={styles.switch}>
          <CustomSwitch
            isChecked={initialState.enableLikes || false}
            handleChange={() => permissionSwitch('enableLikes')}
            label={`${wt(DIALOG.likes)}`}
          />
        </div>
        <div className={styles.switch}>
          <CustomSwitch
            isChecked={initialState.enableDislikes || false}
            handleChange={() => permissionSwitch('enableDislikes')}
            label={`${wt(DIALOG.dislikes)}`}
          />
        </div>
      </div>
    )
  }

  const coverImageBlock = (): React.ReactElement => {
    return (
      <div className={styles.imageBlock}>
        <div className={styles.imageType}>{wt(DIALOG.coverImage)}</div>
        {initialState.coverImage ? (
          <div className={styles.imgBlock}>
            <img
              src={`${AWS_FILE_URL}/${initialState.coverImage}`}
              alt="coverImage"
              width={'100%'}
              height={'100%'}
            />
            <div className={styles.img}>
              <span
                className={styles.imgName}
              >{`${initialState.coverImage}`}</span>
              <WuButton
                variant="secondary"
                aria-label={wt(GLOBAL.removeLogo)}
                onClick={() => setField('coverImage', null)}
                Icon={<WuIcon icon="wm-close" />}
              />
            </div>
          </div>
        ) : (
          <FileUploader
            children={<DialogImageUploader />}
            multiple={false}
            handleChange={(file: File | File[]) =>
              onFileUpload(Array.isArray(file) ? file[0] : file, 'coverImage')
            }
            name="coverImage"
            types={fileTypes}
            disabled={coverFileLoading}
          />
        )}
      </div>
    )
  }

  const logoImageBlock = (): React.ReactElement => {
    return (
      <div className={styles.imageBlock}>
        <div className={styles.imageType}>{wt(DIALOG.logo)}</div>
        {logoFileLoading ? (
          <LoadingPage />
        ) : (
          <>
            {initialState.logoImage ? (
              <div className={styles.imgBlock}>
                <img
                  src={`${AWS_FILE_URL}/${initialState.logoImage}`}
                  alt="logo"
                  width={'100%'}
                  height={'100%'}
                />
                <div className={styles.img}>
                  <span
                    className={styles.imgName}
                  >{`${initialState.logoImage}`}</span>
                  <WuButton
                    variant="secondary"
                    aria-label={wt(GLOBAL.removeLogo)}
                    onClick={() => setField('logoImage', null)}
                    Icon={<WuIcon icon="wm-close" />}
                  />
                </div>
              </div>
            ) : (
              <FileUploader
                children={<DialogImageUploader />}
                multiple={false}
                handleChange={(file: File | File[]) =>
                  onFileUpload(
                    Array.isArray(file) ? file[0] : file,
                    'logoImage',
                  )
                }
                name="logoImage"
                types={fileTypes}
                disabled={logoFileLoading}
              />
            )}
          </>
        )}
      </div>
    )
  }

  // dialogAction/closeModal/wt are all fresh references on every render (wt
  // and useWuShowToast's showToast, a dialogAction dependency, are brand-new
  // closures every call, not just when translations change — same root
  // cause documented in ConfirmModalContent.tsx). Depending on them directly
  // below meant this effect re-fired on every render, calling the parent's
  // setContent with a new element every time, which re-renders the parent
  // with new callbacks, which re-fires this effect — an infinite loop.
  // Reading them via ref keeps the effect keyed only on the state that
  // should actually change the rendered footer.
  const latest = useRef({dialogAction, closeModal, wt})
  latest.current = {dialogAction, closeModal, wt}

  useEffect(() => {
    if (setContent) {
      setContent(
        <div className={styles.footer}>
          <WuButton
            variant="secondary"
            onClick={() => latest.current.closeModal?.()}
          >
            {latest.current.wt(GLOBAL.cancel)}
          </WuButton>
          <WuButton
            variant="primary"
            onClick={() => latest.current.dialogAction()}
            disabled={
              isDialogsLoading ||
              initialState.title?.length < 1 ||
              initialState.questionText?.length < 1 ||
              errors.duplicateTitle
            }
            data-testid="dialog-action-btn"
          >
            {latest.current.wt(GLOBAL.create)}
          </WuButton>
        </div>,
      )
    }
  }, [
    setContent,
    isDialogsLoading,
    initialState.title?.length,
    initialState.questionText?.length,
    errors.duplicateTitle,
  ])

  const buttonBlock = (): React.ReactElement => {
    return (
      <div className={styles.footer}>
        {!uploadImages && (
          <WuButton variant="secondary" onClick={() => closeModal?.()}>
            {wt(GLOBAL.cancel)}
          </WuButton>
        )}
        <WuButton
          className={styles.createBtn}
          onClick={dialogAction}
          loading={
            createDialogMutation.isPending || updateDialogMutation.isPending
          }
          disabled={
            isDialogsLoading ||
            createDialogMutation.isPending ||
            updateDialogMutation.isPending ||
            initialState.title?.length < 1 ||
            initialState.questionText?.length < 1 ||
            errors.duplicateTitle
          }
          data-testid="dialog-action-btn"
        >
          {uploadImages ? `${wt(GLOBAL.update)}` : `${wt(GLOBAL.create)}`}
        </WuButton>
      </div>
    )
  }

  return (
    <div
      className={cx(styles.dialogSetup, uploadImages && styles.dialogSetupEdit)}
    >
      <div className={styles.item}>
        <p className="base-subtitle">
          {wt(DIALOG.title)}
          <Asterisk className={styles.asterisk} />
        </p>
        <div className={styles.inputWrapper}>
          <WuInput
            className={styles.textInput}
            name="title"
            value={initialState.title ?? ''}
            maxLength={80}
            onChange={changeDialogData}
            placeholder={wt(GLOBAL.typeHere) || ''}
            data-testid="dialog-title-input"
          />

          <div className={styles.textLimit}>
            {`${initialState.title?.length}/80`}
          </div>
        </div>
        {errors.duplicateTitle && (
          <span className={styles.duplicateTitle}>
            {wt(DIALOG.duplicateTitleError)}
          </span>
        )}
      </div>
      <div className={styles.item}>
        <p className="base-subtitle">
          {wt(DIALOG.question)}
          <Asterisk className={styles.asterisk} />
        </p>

        <div className={styles.inputWrapper}>
          <WuInput
            className={styles.textInput}
            name="questionText"
            value={initialState.questionText ?? ''}
            maxLength={250}
            onChange={changeDialogData}
            placeholder={wt(GLOBAL.typeHere) || ''}
            data-testid="dialog-questionText-input"
            autoComplete="off"
          />

          <div className={styles.textLimit}>
            {`${initialState.questionText?.length}/250`}
          </div>
        </div>

        {errors.name && <ErrorLabel title={`${wt(GLOBAL.name)}`} />}
      </div>
      {switchBlock()}
      <>
        {coverImageBlock()}
        {logoImageBlock()}
      </>
      {uploadImages && buttonBlock()}
    </div>
  )
}
